import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Package,
  RefreshCcw,
  ShieldAlert,
  TrendingUp,
  TriangleAlert,
  Waves,
} from "lucide-react";
import PageContainer from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocalization } from "@/lib/localization";
import { useAuth } from "@/lib/store";
import { fetchAnalyticsDemandForecast, fetchAnalyticsDemandForecastPage } from "@/lib/shopApi";

const PAGE_LIMIT = 40;

function getBandMeta(copy) {
  return {
    stable: {
      description: copy.bandStableDescription,
      empty: copy.bandStableEmpty,
      icon: CheckCircle2,
      label: copy.steady,
      longLabel: copy.bandStableLong,
      tone: "success",
    },
    watch: {
      description: copy.bandWatchDescription,
      empty: copy.bandWatchEmpty,
      icon: ShieldAlert,
      label: copy.watch,
      longLabel: copy.bandWatchLong,
      tone: "warning",
    },
    irregular: {
      description: copy.bandIrregularDescription,
      empty: copy.bandIrregularEmpty,
      icon: Waves,
      label: copy.irregular,
      longLabel: copy.bandIrregularLong,
      tone: "primary",
    },
  };
}

function normalizeDemandForecast(response) {
  return response?.demandForecast ?? null;
}

function normalizeDemandPage(response) {
  return response?.demandForecast ?? null;
}

function formatGeneratedAt(value, locale, copy) {
  if (!value) return copy.reviewedNow;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return copy.reviewedNow;
  return copy.reviewedAt(
    new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date)
  );
}

function formatNumber(value, locale) {
  if (value === null || value === undefined) return "0";
  return Number(value).toLocaleString(locale, {
    maximumFractionDigits: 2,
  });
}

function createPageState() {
  return {
    error: "",
    hasLoaded: false,
    hasMore: false,
    isLoading: false,
    items: [],
    limit: PAGE_LIMIT,
    nextOffset: 0,
    offset: 0,
    totalCount: 0,
  };
}

export default function AnalyticsDemandForecastList() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.demandForecast;
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stable");
  const [pages, setPages] = useState({
    stable: createPageState(),
    watch: createPageState(),
    irregular: createPageState(),
  });
  const pagesRef = useRef(pages);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  const loadSummary = useCallback(async () => {
    if (!session?.sessionId) {
      setSummaryError(messages.analytics.sessionNotReady);
      setSummaryLoading(false);
      return;
    }

    setSummaryLoading(true);
    setSummaryError("");
    try {
      const response = await fetchAnalyticsDemandForecast(session.sessionId);
      setSummary(normalizeDemandForecast(response));
    } catch (err) {
      setSummaryError(err?.message || copy.errorTitle);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [copy.errorTitle, messages.analytics.sessionNotReady, session?.sessionId]);

  const loadTabPage = useCallback(async (band, offset = 0) => {
    if (!session?.sessionId) return;

    setPages((current) => ({
      ...current,
      [band]: {
        ...current[band],
        error: "",
        isLoading: true,
      },
    }));

    try {
      const response = await fetchAnalyticsDemandForecastPage(session.sessionId, {
        band,
        limit: PAGE_LIMIT,
        offset,
      });
      const nextPage = normalizeDemandPage(response);
      setPages((current) => ({
        ...current,
        [band]: {
          error: "",
          hasLoaded: true,
          hasMore: nextPage?.hasMore ?? false,
          isLoading: false,
          items: nextPage?.items ?? [],
          limit: nextPage?.limit ?? PAGE_LIMIT,
          nextOffset: nextPage?.nextOffset ?? 0,
          offset: nextPage?.offset ?? 0,
          totalCount: nextPage?.totalCount ?? 0,
        },
      }));
    } catch (err) {
      setPages((current) => ({
        ...current,
        [band]: {
          ...current[band],
          error: err?.message || copy.couldNotLoadProducts,
          hasLoaded: true,
          isLoading: false,
        },
      }));
    }
  }, [copy.couldNotLoadProducts, session?.sessionId]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const currentPage = pagesRef.current[activeTab];
    if (!summaryLoading && !summaryError && summary && !currentPage.hasLoaded && !currentPage.isLoading) {
      void loadTabPage(activeTab, 0);
    }
  }, [activeTab, loadTabPage, summary, summaryError, summaryLoading]);

  const stableCount = summary?.stableCount ?? 0;
  const watchCount = summary?.watchCount ?? 0;
  const irregularCount = summary?.irregularCount ?? 0;
  const hasAnyItems = stableCount > 0 || watchCount > 0 || irregularCount > 0;
  const activePage = pages[activeTab];
  const currentPageNumber = activePage.totalCount ? Math.floor(activePage.offset / PAGE_LIMIT) + 1 : 1;
  const totalPages = activePage.totalCount ? Math.max(1, Math.ceil(activePage.totalCount / PAGE_LIMIT)) : 1;

  const refreshAll = useCallback(async () => {
    setPages({
      stable: createPageState(),
      watch: createPageState(),
      irregular: createPageState(),
    });
    await loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (stableCount > 0) {
      setActiveTab("stable");
      return;
    }
    if (watchCount > 0) {
      setActiveTab("watch");
      return;
    }
    if (irregularCount > 0) {
      setActiveTab("irregular");
    }
  }, [irregularCount, stableCount, watchCount]);

  return (
    <PageContainer className="px-4 py-6 sm:px-6 lg:px-8">
      <section className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 mb-3 h-8 px-2 text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/analytics/demand-forecast")}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {copy.title}
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">{copy.listTitle}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {copy.listSubtitle}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            disabled={summaryLoading}
            onClick={() => void refreshAll()}
          >
            <RefreshCcw className={`mr-1.5 h-4 w-4 ${summaryLoading ? "animate-spin" : ""}`} />
            {messages.analytics.refresh}
          </Button>
        </div>

        {summaryLoading ? (
          <div className="luxe-card rounded-lg p-6">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {copy.listLoading}
            </div>
          </div>
        ) : null}

        {!summaryLoading && summaryError ? (
          <div className="luxe-card rounded-lg border-destructive/25 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <TriangleAlert className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">{copy.listErrorTitle}</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{summaryError}</p>
                <Button className="mt-4 h-9" onClick={() => void loadSummary()}>
                  {messages.analytics.tryAgain}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {!summaryLoading && !summaryError && hasAnyItems ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <div className="luxe-card inline-flex rounded-lg p-[3px] shadow-[0_14px_28px_-26px_rgba(15,23,42,0.2)]">
              <TabsList className="grid h-auto w-full min-w-[420px] grid-cols-3 rounded-[12px] border border-border/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.86)_0%,rgba(248,247,243,0.96)_100%)] p-[3px]">
                <TabsTrigger
                  value="stable"
                  className="group h-9 gap-1.5 rounded-[10px] border border-transparent px-2.5 text-[11px] font-semibold text-muted-foreground data-[state=active]:border-success/20 data-[state=active]:bg-[linear-gradient(135deg,rgba(255,255,255,1)_0%,rgba(229,246,236,0.98)_100%)] data-[state=active]:text-success data-[state=active]:shadow-[0_12px_24px_-18px_rgba(21,128,61,0.34)]"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  {copy.steady}
                  <span className="rounded-full border border-transparent bg-background/80 px-1.5 py-0.5 text-[8px] font-semibold text-muted-foreground transition-colors group-data-[state=active]:border-success/20 group-data-[state=active]:bg-success/15 group-data-[state=active]:text-success">
                    {stableCount}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="watch"
                  className="group h-9 gap-1.5 rounded-[10px] border border-transparent px-2.5 text-[11px] font-semibold text-muted-foreground data-[state=active]:border-warning/20 data-[state=active]:bg-[linear-gradient(135deg,rgba(255,255,255,1)_0%,rgba(255,243,228,0.98)_100%)] data-[state=active]:text-warning data-[state=active]:shadow-[0_12px_24px_-18px_rgba(180,83,9,0.3)]"
                >
                  <ShieldAlert className="h-3 w-3" />
                  {copy.watch}
                  <span className="rounded-full border border-transparent bg-background/80 px-1.5 py-0.5 text-[8px] font-semibold text-muted-foreground transition-colors group-data-[state=active]:border-warning/20 group-data-[state=active]:bg-warning/15 group-data-[state=active]:text-warning">
                    {watchCount}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="irregular"
                  className="group h-9 gap-1.5 rounded-[10px] border border-transparent px-2.5 text-[11px] font-semibold text-muted-foreground data-[state=active]:border-primary/20 data-[state=active]:bg-[linear-gradient(135deg,rgba(255,255,255,1)_0%,rgba(236,247,250,0.98)_100%)] data-[state=active]:text-primary data-[state=active]:shadow-[0_12px_24px_-18px_rgba(8,145,178,0.24)]"
                >
                  <Waves className="h-3 w-3" />
                  {copy.irregular}
                  <span className="rounded-full border border-transparent bg-background/80 px-1.5 py-0.5 text-[8px] font-semibold text-muted-foreground transition-colors group-data-[state=active]:border-primary/20 group-data-[state=active]:bg-primary/10 group-data-[state=active]:text-primary">
                    {irregularCount}
                  </span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="stable" className="mt-0">
              <DemandSection
                band="stable"
                errorText={pages.stable.error}
                isLoading={pages.stable.isLoading}
                items={pages.stable.items}
                onRetry={() => void loadTabPage("stable", pages.stable.offset)}
              />
            </TabsContent>
            <TabsContent value="watch" className="mt-0">
              <DemandSection
                band="watch"
                errorText={pages.watch.error}
                isLoading={pages.watch.isLoading}
                items={pages.watch.items}
                onRetry={() => void loadTabPage("watch", pages.watch.offset)}
              />
            </TabsContent>
            <TabsContent value="irregular" className="mt-0">
              <DemandSection
                band="irregular"
                errorText={pages.irregular.error}
                isLoading={pages.irregular.isLoading}
                items={pages.irregular.items}
                onRetry={() => void loadTabPage("irregular", pages.irregular.offset)}
              />
            </TabsContent>

            {activePage.hasLoaded ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/70 bg-background/70 px-4 py-3">
                <div className="text-sm text-muted-foreground">
                  {messages.analytics.pageSummary(currentPageNumber, totalPages, activePage.totalCount)}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    disabled={activePage.isLoading || activePage.offset === 0}
                    onClick={() => void loadTabPage(activeTab, Math.max(0, activePage.offset - PAGE_LIMIT))}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    {messages.analytics.previous}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    disabled={activePage.isLoading || !activePage.hasMore}
                    onClick={() => void loadTabPage(activeTab, activePage.nextOffset)}
                  >
                    {messages.analytics.next}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </Tabs>
        ) : null}

        {!summaryLoading && !summaryError && !hasAnyItems ? (
          <div className="luxe-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/15">
                  <CalendarDays className="h-5 w-5" />
                </div>
                <div>
                  <div className="inline-flex items-center rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                    {copy.salesHistoryBuilding}
                  </div>
                  <h2 className="mt-2 text-xl font-semibold tracking-tight">{copy.noReliableBand}</h2>
                  <p className="mt-1 max-w-xl text-sm leading-5 text-muted-foreground">
                    {copy.noReliableBandDescription}
                  </p>
                </div>
              </div>
              <div className="pt-1 text-sm text-muted-foreground sm:text-right">
                {formatGeneratedAt(summary?.generatedAt, locale, copy)}
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <Metric label={copy.steady} value={formatNumber(stableCount, locale)} strong />
              <Metric label={copy.watch} value={formatNumber(watchCount, locale)} strong />
              <Metric label={copy.irregular} value={formatNumber(irregularCount, locale)} />
            </div>
          </div>
        ) : null}
      </section>
    </PageContainer>
  );
}

function DemandSection({ band, errorText, isLoading, items, onRetry }) {
  const { messages } = useLocalization();
  const meta = getBandMeta(messages.analytics.demandForecast)[band];
  const Icon = meta.icon;
  const toneClass = meta.tone === "success"
    ? "bg-success/10 text-success"
    : meta.tone === "warning"
      ? "bg-warning/10 text-warning"
      : "bg-primary/10 text-primary";

  if (!items.length && isLoading) {
    return (
      <div className="luxe-card rounded-lg p-6">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          {messages.analytics.loadingItems}
        </div>
      </div>
    );
  }

  if (!items.length && errorText) {
    return (
      <div className="luxe-card rounded-lg border-destructive/25 p-6">
        <h2 className="text-base font-semibold tracking-tight">{messages.analytics.thisTabCouldNotLoad}</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{errorText}</p>
        <Button className="mt-4 h-9" onClick={onRetry}>
          {messages.analytics.tryAgain}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold tracking-tight">{meta.longLabel}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{meta.description}</p>
        </div>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>

      {!items.length ? (
        <div className="luxe-card rounded-lg p-5 text-sm leading-6 text-muted-foreground">
          {meta.empty}
        </div>
      ) : (
        <div className="space-y-3">
          {errorText ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-muted-foreground">
              {errorText}
            </div>
          ) : null}
          {items.map((item) => (
            <DemandCard key={item.productId} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function DemandCard({ item }) {
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.demandForecast;
  const bandMeta = getBandMeta(copy);
  const meta = bandMeta[item.demandBand] ?? bandMeta.irregular;
  const toneClass = meta.tone === "success"
    ? "border-success/20 bg-success/10 text-success"
    : meta.tone === "warning"
      ? "border-warning/20 bg-warning/10 text-warning"
      : "border-primary/20 bg-primary/10 text-primary";

  return (
    <article className="luxe-card rounded-lg p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted/70 text-muted-foreground">
            <Package className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold tracking-tight">{item.productName}</h3>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.recommendation}</p>
          </div>
        </div>
        <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${toneClass}`}>
          {meta.longLabel}
        </span>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-4">
        <Metric label={copy.avgWeekly} value={formatNumber(item.averageWeeklyDemand, locale)} strong />
        <Metric label={copy.weeksWithSales} value={formatNumber(item.weeksWithSales, locale)} strong />
        <Metric label={copy.soldInWindow} value={formatNumber(item.soldLastWindow, locale)} />
        <Metric label={copy.variability} value={formatNumber(item.variabilityScore, locale)} />
      </div>
    </article>
  );
}

function Metric({ label, value, strong = false }) {
  const { messages } = useLocalization();
  const copy = messages.analytics.demandForecast;
  const icon = label === copy.steady
    ? <CheckCircle2 className="h-3.5 w-3.5" />
    : label === copy.watch
      ? <ShieldAlert className="h-3.5 w-3.5" />
      : label === copy.irregular
        ? <Activity className="h-3.5 w-3.5" />
        : label === copy.avgWeekly
          ? <TrendingUp className="h-3.5 w-3.5" />
          : null;

  return (
    <div className="rounded-md border border-border/70 bg-background/60 px-3 py-3">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={`mt-1 font-mono text-sm tabular-nums ${strong ? "font-semibold text-foreground" : "font-medium text-foreground/85"}`}>
        {value}
      </div>
    </div>
  );
}
